import { UserWithInfo, Workgroup, SurveyWorkgroup } from '@g17eco/types/workgroup';
import { g17ecoApi, transformResponse } from './g17ecoApi';
import { SurveyPermission } from '../types/permission';
import { Tag as SurveyUsersTag } from './survey-users';

export enum Tag {
  Workgroups = 'workgroups',
  Workgroup = 'workgroup',
  SurveyWorkgroups = 'survey-workgroups',
}

const getUrl = (initiativeId: string) => `/initiatives/${initiativeId}/workgroups`;
const getSurveyWorkgroupsUrl = (surveyId: string) => `/surveys/${surveyId}/workgroups`;

export type CreateWorkgroup = Pick<Workgroup, 'name' | 'description' | 'icon' | 'color'>;

export type DelegateSurveyWorkgroupParams = {
  surveyId: string;
  workgroupIds: string[];
  permission: Pick<SurveyPermission, 'access' | 'scope' | 'roles'>;
};

export type UpdateSurveyWorkgroupParams = Pick<DelegateSurveyWorkgroupParams, 'surveyId' | 'permission'> & {
  workgroupId: string;
};

export const WorkgroupsApi = g17ecoApi
  .enhanceEndpoints({
    addTagTypes: [...Object.values(Tag), SurveyUsersTag.SurveyUsers],
  })
  .injectEndpoints({
    endpoints: (build) => ({
      getWorkgroups: build.query<Workgroup[], string>({
        transformResponse,
        query: (initiativeId) => ({
          url: getUrl(initiativeId),
          method: 'get',
        }),
        providesTags: (_result, _error, initiativeId) => {
          return [{ type: Tag.Workgroups, id: initiativeId }];
        },
      }),
      getWorkgroup: build.query<Workgroup<UserWithInfo>, { initiativeId: string; workgroupId: string }>({
        transformResponse,
        query: ({ initiativeId, workgroupId }) => ({
          url: `${getUrl(initiativeId)}/${workgroupId}`,
          method: 'get',
        }),
        providesTags: (_result, _error, { workgroupId }) => {
          return [{ type: Tag.Workgroup, id: workgroupId }];
        },
      }),
      createWorkgroup: build.mutation<Workgroup, { initiativeId: string; data: CreateWorkgroup }>({
        invalidatesTags: (_result, _error, { initiativeId }) => [{ type: Tag.Workgroups, id: initiativeId }],
        transformResponse,
        query: ({ initiativeId, data }) => ({
          url: getUrl(initiativeId),
          method: 'post',
          data,
        }),
      }),
      updateWorkgroup: build.mutation<Workgroup, { initiativeId: string; workgroupId: string; data: CreateWorkgroup }>({
        invalidatesTags: (_result, _error, { initiativeId, workgroupId }) => [
          { type: Tag.Workgroups, id: initiativeId },
          { type: Tag.Workgroup, id: workgroupId },
          { type: SurveyUsersTag.SurveyUsers },
          { type: Tag.SurveyWorkgroups },
        ],
        transformResponse,
        query: ({ initiativeId, workgroupId, data }) => ({
          url: `${getUrl(initiativeId)}/${workgroupId}`,
          method: 'put',
          data,
        }),
      }),
      deleteWorkgroup: build.mutation<Workgroup, { initiativeId: string; workgroupId: string }>({
        invalidatesTags: (_result, _error, { initiativeId }) => [
          { type: Tag.Workgroups, id: initiativeId },
          { type: SurveyUsersTag.SurveyUsers },
          { type: Tag.SurveyWorkgroups },
        ],
        transformResponse,
        query: ({ initiativeId, workgroupId }) => ({
          url: `${getUrl(initiativeId)}/${workgroupId}`,
          method: 'delete',
        }),
      }),
      duplicateWorkgroup: build.mutation<Workgroup, { initiativeId: string; workgroupId: string }>({
        invalidatesTags: (_result, _error, { initiativeId }) => [{ type: Tag.Workgroups, id: initiativeId }],
        transformResponse,
        query: ({ initiativeId, workgroupId }) => ({
          url: `${getUrl(initiativeId)}/${workgroupId}/duplicate`,
          method: 'post',
        }),
      }),
      addWorkgroupUsers: build.mutation<Workgroup, { initiativeId: string; workgroupId: string; userIds: string[] }>({
        invalidatesTags: (_result, _error, { initiativeId, workgroupId }) => [
          { type: Tag.Workgroups, id: initiativeId },
          { type: Tag.Workgroup, id: workgroupId },
          { type: SurveyUsersTag.SurveyUsers },
          { type: Tag.SurveyWorkgroups },
        ],
        transformResponse,
        query: ({ initiativeId, workgroupId, userIds }) => ({
          url: `${getUrl(initiativeId)}/${workgroupId}/users`,
          method: 'put',
          data: { userIds },
        }),
      }),
      removeWorkgroupUser: build.mutation<Workgroup, { initiativeId: string; workgroupId: string; userId: string }>({
        invalidatesTags: (_result, _error, { initiativeId, workgroupId }) => [
          { type: Tag.Workgroups, id: initiativeId },
          { type: Tag.Workgroup, id: workgroupId },
          { type: SurveyUsersTag.SurveyUsers },
          { type: Tag.SurveyWorkgroups },
        ],
        transformResponse,
        query: ({ initiativeId, workgroupId, userId }) => ({
          url: `${getUrl(initiativeId)}/${workgroupId}/users/${userId}`,
          method: 'delete',
        }),
      }),
      getSurveyWorkgroups: build.query<SurveyWorkgroup[], string>({
        transformResponse,
        query: (surveyId) => ({
          url: getSurveyWorkgroupsUrl(surveyId),
          method: 'get',
        }),
        providesTags: (_result, _error, surveyId) => {
          return [{ type: Tag.SurveyWorkgroups, id: surveyId }];
        },
      }),
      delegateSurveyWorkgroup: build.mutation<SurveyPermission[], DelegateSurveyWorkgroupParams>({
        invalidatesTags: (_result, _error, { surveyId }) => [
          { type: Tag.SurveyWorkgroups, id: surveyId },
          { type: SurveyUsersTag.SurveyUsers, id: surveyId },
        ],
        transformResponse,
        query: ({ surveyId, ...data }) => ({
          url: getSurveyWorkgroupsUrl(surveyId),
          method: 'post',
          data,
        }),
      }),
      updateSurveyWorkgroup: build.mutation<SurveyPermission[], UpdateSurveyWorkgroupParams>({
        invalidatesTags: (_result, _error, { surveyId }) => [
          { type: Tag.SurveyWorkgroups, id: surveyId },
          { type: SurveyUsersTag.SurveyUsers, id: surveyId },
        ],
        transformResponse,
        query: ({ surveyId, ...data }) => ({
          url: getSurveyWorkgroupsUrl(surveyId),
          method: 'put',
          data,
        }),
      }),
      removeSurveyWorkgroup: build.mutation<SurveyPermission[], { surveyId: string; workgroupId: string }>({
        invalidatesTags: (_result, _error, { surveyId }) => [
          { type: Tag.SurveyWorkgroups, id: surveyId },
          { type: SurveyUsersTag.SurveyUsers, id: surveyId },
        ],
        transformResponse,
        query: ({ surveyId, workgroupId }) => ({
          url: getSurveyWorkgroupsUrl(surveyId),
          method: 'delete',
          data: { workgroupIds: [workgroupId] },
        }),
      }),
    }),
  });

export const {
  useGetWorkgroupsQuery,
  useGetWorkgroupQuery,
  useCreateWorkgroupMutation,
  useUpdateWorkgroupMutation,
  useDeleteWorkgroupMutation,
  useDuplicateWorkgroupMutation,
  useAddWorkgroupUsersMutation,
  useRemoveWorkgroupUserMutation,
  useGetSurveyWorkgroupsQuery,
  useDelegateSurveyWorkgroupMutation,
  useUpdateSurveyWorkgroupMutation,
  useRemoveSurveyWorkgroupMutation,
} = WorkgroupsApi;
